import * as Dialog from "@radix-ui/react-dialog";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";

interface CreateWallOfLoveModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess: (wallId: string) => void;
	onSubmit: (data: WallOfLoveFormValues) => Promise<{ success: boolean; data?: { id: string } }>;
}

export type WallOfLoveFormValues = z.infer<typeof wallOfLoveFormSchema>;

const wallOfLoveFormSchema = z.object({
	title: z.string().min(1, "Wall of Love title is required").max(200, "Max 200 characters"),
});

export default function CreateWallOfLoveModal({ open, onOpenChange, onSuccess, onSubmit }: CreateWallOfLoveModalProps) {
	const [error, setError] = useState<string | null>(null);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors, isSubmitting },
	} = useForm<WallOfLoveFormValues>({
		resolver: zodResolver(wallOfLoveFormSchema),
		defaultValues: {
			title: "",
		},
	});

	const handleFormSubmit = async (data: WallOfLoveFormValues) => {
		setError(null);
		try {
			const result = await onSubmit(data);
			if (result.success && result.data) {
				onSuccess(result.data.id);
				handleReset();
				onOpenChange(false);
			} else {
				setError("Failed to create wall of love. Please try again.");
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : "An error occurred while creating the wall of love.");
		}
	};

	const handleReset = () => {
		reset();
		setError(null);
	};

	const handleOpenChange = (newOpen: boolean) => {
		if (!newOpen) {
			handleReset();
		}
		onOpenChange(newOpen);
	};

	return (
		<Dialog.Root open={open} onOpenChange={handleOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 modal-overlay" />
				<Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-lg modal-content overflow-hidden">
					<form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col max-h-[80vh]">
						{/* Header */}
						<div className="px-6 pt-6 pb-4 border-b border-gray-200">
							<Dialog.Title className="text-base font-semibold text-gray-900 leading-base">Create Wall of Love</Dialog.Title>
							<Dialog.Description className="text-sm text-gray-500 leading-sm mt-1">
								Enter a title for your wall of love. You'll be taken to the page builder to customize it.
							</Dialog.Description>
						</div>

						{/* Content */}
						<div className="overflow-y-auto px-6 py-4">
							<div className="space-y-5">
								<Input
									id="wall-of-love-title"
									type="text"
									label="Wall of Love Title"
									placeholder="Enter wall of love title"
									required
									disabled={isSubmitting}
									error={errors.title?.message}
									{...register("title")}
								/>
							</div>
						</div>

						{/* Footer */}
						<div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
							<Dialog.Close asChild>
								<Button type="button" variant="soft">
									Cancel
								</Button>
							</Dialog.Close>
							<div className="flex items-center gap-3">
								{error && <p className="text-sm text-red-600 leading-sm">{error}</p>}
								<Button type="submit" variant="brand" disabled={isSubmitting}>
									{isSubmitting ? "Creating..." : "Create & Go to Builder"}
								</Button>
							</div>
						</div>
					</form>

					<Dialog.Close asChild>
						<button className="absolute top-7 right-6 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
							<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</Dialog.Close>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
